import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export const UploadPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', null

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format invalide', {
        description: 'Veuillez charger un fichier PDF uniquement.',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadedFile(file);
    setUploadStatus(null);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 85) {
          clearInterval(progressInterval);
          return 85;
        }
        return prev + 15;
      });
    }, 300);

    try {
      // Upload to backend
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/reports/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeader(),
        },
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus('success');
      
      const shareToken = response.data.report.share_token;
      
      toast.success('Rapport transformé avec succès!', {
        description: 'Votre rapport TICFE a été analysé et modernisé.',
      });
      
      // Set flag to indicate we're coming from upload (admin view)
      sessionStorage.setItem('from_upload', 'true');
      
      // Navigate to report view after a short delay
      setTimeout(() => {
        navigate(`/report/${shareToken}`);
      }, 1500);
      
    } catch (error) {
      clearInterval(progressInterval);
      setUploadStatus('error');
      setIsUploading(false);
      toast.error('Erreur de transformation', {
        description: error.response?.data?.detail || 'Une erreur est survenue lors de la transformation du rapport.',
      });
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl font-bold font-display mb-4">
              Charger un <span className="gradient-text">Rapport TICFE</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Glissez-déposez votre rapport PDF ou cliquez pour le sélectionner. 
              Notre système analysera et transformera automatiquement votre rapport.
            </p>
          </div>

          <Card className="border-2 border-dashed border-border hover:border-primary/50 transition-colors duration-300">
            <CardContent className="p-8 sm:p-12">
              <div
                className={`relative rounded-xl border-2 border-dashed transition-all duration-300 ${
                  isDragging
                    ? 'border-primary bg-accent/50 scale-105'
                    : 'border-border hover:border-primary/50 hover:bg-accent/20'
                } ${
                  isUploading ? 'pointer-events-none' : 'cursor-pointer'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploading}
                />
                
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  {!isUploading && !uploadStatus && (
                    <>
                      <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center mb-6">
                        <Upload className="w-10 h-10 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">
                        Glissez-déposez votre rapport PDF
                      </h3>
                      <p className="text-muted-foreground mb-6">ou cliquez pour parcourir</p>
                      <Button variant="outline" size="lg">
                        Sélectionner un fichier
                      </Button>
                    </>
                  )}
                  
                  {isUploading && uploadStatus === null && (
                    <>
                      <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center mb-6 animate-pulse-glow">
                        <Loader2 className="w-10 h-10 text-primary animate-spin" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Transformation en cours...</h3>
                      <p className="text-muted-foreground mb-6">{uploadedFile?.name}</p>
                      <div className="w-full max-w-md">
                        <Progress value={uploadProgress} className="h-2" />
                        <p className="text-sm text-muted-foreground text-center mt-2">
                          {uploadProgress}% complété
                        </p>
                      </div>
                    </>
                  )}
                  
                  {uploadStatus === 'success' && (
                    <>
                      <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle2 className="w-10 h-10 text-success" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-success">Rapport transformé !</h3>
                      <p className="text-muted-foreground">Redirection vers votre rapport...</p>
                    </>
                  )}
                  
                  {uploadStatus === 'error' && (
                    <>
                      <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                        <AlertCircle className="w-10 h-10 text-destructive" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-destructive">Erreur de transformation</h3>
                      <p className="text-muted-foreground mb-6">Veuillez réessayer</p>
                      <Button onClick={() => {
                        setUploadStatus(null);
                        setIsUploading(false);
                        setUploadedFile(null);
                      }}>
                        Réessayer
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <InfoCard
              icon={<FileText className="w-6 h-6" />}
              title="Format PDF"
              description="Nous acceptons uniquement les rapports au format PDF."
            />
            <InfoCard
              icon={<CheckCircle2 className="w-6 h-6" />}
              title="Transformation Rapide"
              description="Votre rapport est analysé et transformé en quelques secondes."
            />
            <InfoCard
              icon={<Upload className="w-6 h-6" />}
              title="Jusqu'à 50 MB"
              description="Taille maximale de fichier acceptée: 50 MB."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ icon, title, description }) => {
  return (
    <Card className="text-center">
      <CardContent className="p-6 flex flex-col items-center">
        <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4 text-primary">
          {icon}
        </div>
        <h4 className="font-semibold mb-2">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
};

export default UploadPage;
